<!-- app-subdomain-creators/src/Components/NewsfeedCreatorYourPostsTab.vue -->
<template>
  <div class="lg:w-[1175px] mx-auto pb-10 pt-5">
    <!-- Loading state -->
    <div
      v-if="isLoading && !yourPosts.length"
      class="space-y-4"
    >
      <div
        v-for="n in 3"
        :key="n"
        class="h-32 rounded-xl bg-gradient-to-r from-zinc-200 via-zinc-100 to-zinc-200 animate-pulse"
      ></div>
    </div>
    <!-- Empty state -->
    <div
      v-else-if="!yourPosts.length"
      class="flex flex-col items-center justify-center py-16 text-center text-zinc-500"
    >
      <p class="text-lg font-semibold mb-2">You haven’t posted anything yet</p>
      <p class="text-sm text-zinc-400 max-w-md">
        Create a post from the Discussion tab. Your posts will show up here in
        one place, with your newest posts at the top.
      </p>
    </div>
    <!-- Your posts list -->
    <div v-else class="space-y-4 max-w-[700px]">
      <div
        v-for="post in yourPosts"
        :key="post.id"
        class="relative"
      >
        <NewsfeedCreatorPost
          :post="post"
          :featureInstanceId="featureInstanceId"
          :postingAsName="postingAsName"
          :postingAsAvatarUrl="postingAsAvatarUrl"
          context="your-posts"
          @edit-post="handleEditPostFromYourPosts"
          @save-post="handleSavePostFromYourPosts"
          @unsave-post="handleUnsavePostFromYourPosts"
          @delete-post="handleDeletePostFromYourPosts" 
        />
      </div>
      <div
        v-if="hasMore"
        ref="infiniteScrollSentinel"
        class="flex justify-center pt-4 h-16"
      >
        <span class="text-sm text-zinc-400">
          {{ isLoadingMore ? 'Loading...' : 'Scroll for more' }}
        </span>
      </div>
    </div>
  </div>
  <NewsfeedDeletePostConfirmModal
  :is-open="deleteModalOpen"
  :is-submitting="isDeletingPost"
  @cancel="handleCancelDelete"
  @confirm="handleConfirmDelete"
/>
</template>
<script setup>
import { ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import NewsfeedCreatorPost from '@/Components/NewsfeedCreatorPost.vue';
import NewsfeedDeletePostConfirmModal from '@/Components/NewsfeedDeletePostConfirmModal.vue';
const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL;
const props = defineProps({
  featureInstanceId: {
    type: [Number, String],
    default: null,
  },
  postingAsName: {
    type: String,
    default: 'You',
  },
  postingAsAvatarUrl: {
    type: String,
    default: null,
  },
});
const emit = defineEmits(['open-edit-post']);
const yourPosts = ref([]);
const isLoading = ref(false);
const isLoadingMore = ref(false);
const hasMore = ref(false);
const limit = 10;
const offset = ref(0);
const infiniteScrollSentinel = ref(null);
let infiniteObserver = null;
let sentinelReadyForNextLoad = true;
const INFINITE_LOAD_COOLDOWN_MS = 500;
let lastInfiniteLoadAt = 0;
let inflightAppendOffset = null;
// 🔹 Delete modal state (same pattern as Discussion tab)
const deleteModalOpen = ref(false);
const deleteTargetPostId = ref(null);
const isDeletingPost = ref(false);
const defaultAvatar = new URL(
  '@/assets/media/images/default-avatar.png',
  import.meta.url
).href;
/**
 * 🔹 Local cache of saved post IDs for this instance
 *    (used so Your Posts knows which of *your* posts are saved)
 */
let savedIds = new Set();

const handleEditPostFromYourPosts = ({ postId }) => {
  if (!postId) return;
  const postToEdit = yourPosts.value.find((p) => String(p.id) === String(postId));
  if (!postToEdit) return;
  emit('open-edit-post', { post: { ...postToEdit } });
};
const buildCoverImageUrl = (fileId) => {
  if (!fileId) return null;
  return `${apiProxyUrl}/api/image/${fileId}`;
};

const buildCreatorNewsfeedPlaylistUrl = (fileId) => {
  if (!fileId) return null;
  const params = new URLSearchParams();
  const featureId = String(props.featureInstanceId || '').trim();
  if (featureId) params.set('featureInstanceId', featureId);
  const qs = params.toString();
  return `${apiProxyUrl}/api/creator-newsfeed/stream-hls/playlist/${encodeURIComponent(
    String(fileId)
  )}${qs ? `?${qs}` : ''}`;
};
/**
 * 🔹 Fetch IDs of posts saved by this creator for this newsfeed instance
 *     via the existing /saved-posts endpoint.
 */
async function fetchSavedIdsForInstance(instanceId) {
  savedIds = new Set();
  if (!instanceId) return;
  try {
    const params = new URLSearchParams();
    params.set('limit', '500');              // plenty for IDs
    params.set('offset', '0');
    params.set('featureInstanceId', String(instanceId));
    const url = `${apiProxyUrl}/api/creator-newsfeed/saved-posts?${params.toString()}`;
    const res = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      console.error(
        '[NewsfeedCreatorYourPostsTab] fetchSavedIdsForInstance backend NOT OK:',
        res.status,
        text
      );
      return;
    }
    const data = await res.json();
    const rawPosts = Array.isArray(data.posts) ? data.posts : [];
    rawPosts.forEach((p) => {
      if (p && (p.id !== undefined && p.id !== null)) {
        savedIds.add(String(p.id));
      }
    });
    console.log('[NewsfeedCreatorYourPostsTab] savedIds for instance', {
      instanceId,
      ids: Array.from(savedIds),
    });
  } catch (err) {
    console.error(
      '[NewsfeedCreatorYourPostsTab] fetchSavedIdsForInstance error:',
      err
    );
  }
}
// 🔹 Normalize posts exactly like Discussion tab, but scoped to "your posts"
function normalizeYourPost(p) {
  if (!p) return null;
  const currentUserName = props.postingAsName || 'You';
  const currentUserAvatar = props.postingAsAvatarUrl || defaultAvatar;
  const rawCreated = p.createdAt || p.created_at || null;
  const createdDate = rawCreated ? new Date(rawCreated) : null;
  const createdAtMs =
    createdDate && !Number.isNaN(createdDate.getTime())
      ? createdDate.getTime()
      : 0;
  // Attachments
  const attachments = Array.isArray(p.attachments)
    ? p.attachments
        .map((att) => {
          if (!att) return null;
          const fileId = att.fileId || att.file_id || att.mediaFileId || null;
          const normalized = fileId ? { ...att, fileId: String(fileId) } : { ...att };

          const mimeType = String(
            normalized?.mimeType || normalized?.mime || normalized?.contentType || ''
          )
            .toLowerCase()
            .trim();
          const fileType = String(normalized?.fileType || normalized?.type || '')
            .toLowerCase()
            .trim();
          const hasHls =
            String(normalized?.hlsPlaylistUrl || normalized?.hls_playlist_url || '').trim().length > 0;
          const isLikelyVideo = mimeType.startsWith('video/') || fileType === 'video' || hasHls;

          if (!normalized.url) {
            normalized.url = normalized?.fileUrl || normalized?.file_url || null;
          }

          if (isLikelyVideo && !normalized.videoUrl && !normalized.secureVideoUrl) {
            normalized.videoUrl =
              (normalized?.fileId
                ? buildCreatorNewsfeedPlaylistUrl(normalized.fileId)
                : null) ||
              normalized?.hlsPlaylistUrl ||
              normalized?.hls_playlist_url ||
              normalized?.url ||
              normalized?.fileUrl ||
              normalized?.file_url ||
              null;
          }

          if (!normalized.thumbnailUrl && normalized?.fileId) {
            normalized.thumbnailUrl = buildCoverImageUrl(normalized.fileId);
          }

          return normalized;
        })
        .filter(Boolean)
    : [];
  const coverMediaFileId =
    p.coverMediaFileId ||
    p.cover_media_file_id ||
    (attachments.length ? attachments[0].fileId : null);
  const imageUrl = coverMediaFileId
    ? buildCoverImageUrl(coverMediaFileId)
    : null;
  // Reactions summary
  const reactionsSummary = p.reactionsSummary || p.reactions || {
    total: 0,
    like: 0,
    love: 0,
    care: 0,
    haha: 0,
    wow: 0,
    sad: 0,
    angry: 0,
  };
  // Reaction user lists → names
  const reactionUsersByType = p.reactionUsersByType || {
    like: [],
    love: [],
    care: [],
    haha: [],
    wow: [],
    sad: [],
    angry: [],
  };
  const reactionNamesByType = {};
  for (const key of ['like', 'love', 'care', 'haha', 'wow', 'sad', 'angry']) {
    const arr = Array.isArray(reactionUsersByType[key])
      ? reactionUsersByType[key]
      : [];
    reactionNamesByType[key] = arr
      .map((u) => (u && typeof u.name === 'string' ? u.name.trim() : ''))
      .filter(Boolean);
  }
  const reactionNamesLegacy = Object.values(reactionNamesByType).flat();
  // 🔹 Combine backend flag + savedIds set
  const isSavedFromBackend =
    p.isSaved ?? p.is_saved ?? p.saved ?? false;
  const isSavedFromSet =
    p.id !== undefined && p.id !== null
      ? savedIds.has(String(p.id))
      : false;
  const isSaved = !!(isSavedFromBackend || isSavedFromSet);
  return {
    id: p.id,
    authorName: p.postedAsName || currentUserName,
    authorAvatar: p.postedAsAvatarUrl || defaultAvatar,
    createdAt: rawCreated,
    createdAtMs,
    timeAgo: rawCreated || '',
    body: p.content || p.body || '',
    reactionsSummary,
    reactionsCount: Number(reactionsSummary.total) || 0,
    commentsCount: p.commentsCount ?? 0,
    sendCount: 0,
    reactionNamesByType,
    reactionNames: reactionNamesLegacy,
    hasMedia: attachments.length > 0 || !!coverMediaFileId,
    imageUrl,
    coverMediaFileId,
    attachments,
    poll: p.poll || p.pollData || p.postPoll || null,
    postType: p.postType || p.post_type || (p.poll ? 'poll' : 'text'),
    pollOptions: Array.isArray(p.pollOptions) ? p.pollOptions : null,
    currentUserName,
    currentUserAvatar,
    // 🔹 final truth for bookmark + menu
    isSaved,
    raw: p,
  };
}

function mergeUpdatedPostWithExisting(existingPost, normalizedPost, rawUpdate = null) {
  const existing = existingPost || {};
  const normalized = normalizedPost || {};
  const raw = rawUpdate && typeof rawUpdate === 'object' ? rawUpdate : {};

  const merged = { ...existing, ...normalized };

  if (!merged.createdAt) merged.createdAt = existing.createdAt || null;
  if (!merged.timeAgo) merged.timeAgo = existing.timeAgo || merged.createdAt || '';
  if (!merged.authorName) merged.authorName = existing.authorName || null;
  if (!merged.authorAvatar) merged.authorAvatar = existing.authorAvatar || null;

  const hasRawAttachments = Array.isArray(raw.attachments);
  const hasUsableRawAttachments =
    hasRawAttachments &&
    raw.attachments.some((att) => {
      if (!att || typeof att !== 'object') return false;
      // fileId-only attachment objects are common in edit payloads and should not
      // be treated as a full media replacement.
      return !!(
        att.url ||
        att.fileUrl ||
        att.file_url ||
        att.videoUrl ||
        att.secureVideoUrl ||
        att.hlsPlaylistUrl ||
        att.hls_playlist_url ||
        att.thumbnailUrl ||
        att.posterUrl ||
        att.mimeType ||
        att.mime ||
        att.contentType ||
        att.fileType ||
        att.type
      );
    });
  const rawCoverMediaValue =
    raw.coverMediaFileId ??
    raw.cover_media_file_id ??
    null;
  const existingCoverMediaValue = existing.coverMediaFileId ?? null;
  const hasRawCoverMedia =
    (Object.prototype.hasOwnProperty.call(raw, 'coverMediaFileId') ||
      Object.prototype.hasOwnProperty.call(raw, 'cover_media_file_id')) &&
    String(rawCoverMediaValue || '') !== String(existingCoverMediaValue || '');
  const hasRawMediaAdds = Array.isArray(raw.mediaAddFileIds) && raw.mediaAddFileIds.length > 0;
  const hasRawMediaRemoves =
    Array.isArray(raw.mediaRemoveFileIds) && raw.mediaRemoveFileIds.length > 0;
  const hasAnyRawMediaMutation =
    hasRawCoverMedia ||
    hasUsableRawAttachments ||
    hasRawMediaAdds ||
    hasRawMediaRemoves;

  const normalizedHasMedia =
    !!normalized.coverMediaFileId ||
    !!normalized.imageUrl ||
    (Array.isArray(normalized.attachments) && normalized.attachments.length > 0) ||
    !!normalized.hasMedia;
  const existingHasMedia =
    !!existing.coverMediaFileId ||
    !!existing.imageUrl ||
    (Array.isArray(existing.attachments) && existing.attachments.length > 0) ||
    !!existing.hasMedia;

  if (!hasAnyRawMediaMutation || (!normalizedHasMedia && existingHasMedia && !hasRawMediaRemoves)) {
    merged.attachments = Array.isArray(existing.attachments) ? existing.attachments : [];
    merged.coverMediaFileId = existing.coverMediaFileId || null;
    merged.imageUrl = existing.imageUrl || null;
    merged.hasMedia =
      !!existing.hasMedia ||
      !!existing.coverMediaFileId ||
      (Array.isArray(existing.attachments) && existing.attachments.length > 0);
  }

  const hasRawPollMutation =
    Object.prototype.hasOwnProperty.call(raw, 'poll') ||
    Object.prototype.hasOwnProperty.call(raw, 'postType') ||
    Object.prototype.hasOwnProperty.call(raw, 'post_type') ||
    Object.prototype.hasOwnProperty.call(raw, 'pollOptions') ||
    Object.prototype.hasOwnProperty.call(raw, 'poll_options');
  const normalizedHasPoll =
    !!normalized.poll ||
    String(normalized.postType || '').toLowerCase() === 'poll' ||
    (Array.isArray(normalized.pollOptions) && normalized.pollOptions.length > 0);
  const existingHasPoll =
    !!existing.poll ||
    String(existing.postType || '').toLowerCase() === 'poll' ||
    (Array.isArray(existing.pollOptions) && existing.pollOptions.length > 0);

  if (!hasRawPollMutation || (!normalizedHasPoll && existingHasPoll)) {
    merged.poll = existing.poll || null;
    merged.postType = existing.postType || merged.postType || null;
    merged.pollOptions = Array.isArray(existing.pollOptions)
      ? existing.pollOptions
      : merged.pollOptions || null;
  }

  return merged;
}

async function addOrUpdatePostFromCreate(newPost) {
  const post = newPost && typeof newPost === 'object' ? newPost : null;
  if (!post) return false;

  const currentInstanceId = Number(props.featureInstanceId);
  const incomingInstanceId = Number(
    post.instanceId ?? post.featureInstanceId ?? post.instance_id
  );
  if (
    Number.isFinite(currentInstanceId) &&
    Number.isFinite(incomingInstanceId) &&
    currentInstanceId !== incomingInstanceId
  ) {
    return false;
  }

  const status = String(post.status || post.postStatus || '').toLowerCase();
  if (status && status !== 'published') return false;

  const normalized = normalizeYourPost(post);
  if (!normalized?.id) return false;

  const idx = yourPosts.value.findIndex((p) => String(p.id) === String(normalized.id));
  if (idx >= 0) {
    const next = [...yourPosts.value];
    next[idx] = mergeUpdatedPostWithExisting(next[idx], normalized, post);
    yourPosts.value = next;
  } else {
    yourPosts.value = [normalized, ...yourPosts.value];
  }

  yourPosts.value.sort((a, b) => (b.createdAtMs || 0) - (a.createdAtMs || 0));
  return true;
}

async function addOrUpdatePostFromUpdate(updatedPost) {
  const post = updatedPost && typeof updatedPost === 'object' ? updatedPost : null;
  if (!post) return false;

  const status = String(post.status || post.postStatus || '').toLowerCase();
  if (status && status !== 'published') return false;

  const normalized = normalizeYourPost(post);
  if (!normalized?.id) return false;

  const idx = yourPosts.value.findIndex((p) => String(p.id) === String(normalized.id));
  if (idx >= 0) {
    const next = [...yourPosts.value];
    next[idx] = mergeUpdatedPostWithExisting(next[idx], normalized, post);
    yourPosts.value = next;
  } else {
    yourPosts.value = [normalized, ...yourPosts.value];
  }

  yourPosts.value.sort((a, b) => (b.createdAtMs || 0) - (a.createdAtMs || 0));
  return true;
}
async function fetchYourPosts(options = { reset: false }) {
  const { reset } = options || {};
  if (!props.featureInstanceId) {
    yourPosts.value = [];
    hasMore.value = false;
    inflightAppendOffset = null;
    return;
  }
  if (reset) {
    offset.value = 0;
  }
  const isFirstPage = offset.value === 0;
  if (isFirstPage) {
    isLoading.value = true;
  } else {
    isLoadingMore.value = true;
  }
  try {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    params.set('offset', String(offset.value));
    params.set('featureInstanceId', String(props.featureInstanceId));
    const url = `${apiProxyUrl}/api/creator-newsfeed/my-posts?${params.toString()}`;
    console.log('[NewsfeedCreatorYourPostsTab] fetchYourPosts()', {
      featureInstanceId: props.featureInstanceId,
      offset: offset.value,
      url,
    });
    const res = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      console.error(
        '[NewsfeedCreatorYourPostsTab] my-posts backend NOT OK:',
        res.status,
        text
      );
      if (isFirstPage) {
        yourPosts.value = [];
      }
      hasMore.value = false;
      return;
    }
    const data = await res.json();
    console.log(
      '[NewsfeedCreatorYourPostsTab] my-posts backend payload:',
      data
    );
    const rawPosts = Array.isArray(data.posts) ? data.posts : [];
    const normalized = rawPosts
      .map((p) => normalizeYourPost(p))
      .filter(Boolean);
    if (reset) {
      yourPosts.value = normalized;
    } else {
      yourPosts.value = [...yourPosts.value, ...normalized];
    }
    yourPosts.value.sort((a, b) => {
      const aKey = a.createdAtMs || 0;
      const bKey = b.createdAtMs || 0;
      return bKey - aKey;
    });
    hasMore.value = normalized.length === limit;
    if (normalized.length > 0) {
      offset.value += normalized.length;
    }
  } catch (err) {
    console.error('[NewsfeedCreatorYourPostsTab] fetchYourPosts error:', err);
    if (offset.value === 0) {
      yourPosts.value = [];
    }
    hasMore.value = false;
  } finally {
    isLoading.value = false;
    isLoadingMore.value = false;
    if (!isFirstPage) {
      inflightAppendOffset = null;
    }
  }
}
function loadMore() {
  if (!hasMore.value || isLoadingMore.value) return Promise.resolve();
  return fetchYourPosts({ reset: false });
}

function setupInfiniteScrollObserver() {
  if (infiniteObserver) {
    infiniteObserver.disconnect();
    infiniteObserver = null;
  }
  if (!infiniteScrollSentinel.value) return;

  const rootEl =
    document.querySelector('.app-scroll') ||
    document.querySelector('#app-scroll') ||
    null;

  infiniteObserver = new IntersectionObserver(
    (entries) => {
      const entry = entries[0];
      if (!entry) return;

      if (!entry.isIntersecting) {
        sentinelReadyForNextLoad = true;
        return;
      }

      if (!sentinelReadyForNextLoad) return;
      if (!hasMore.value || isLoading.value || isLoadingMore.value) return;
      if (inflightAppendOffset === offset.value) return;
      if (Date.now() - lastInfiniteLoadAt < INFINITE_LOAD_COOLDOWN_MS) return;

      sentinelReadyForNextLoad = false;
      inflightAppendOffset = offset.value;
      lastInfiniteLoadAt = Date.now();
      loadMore().finally(() => {
        inflightAppendOffset = null;
      });
    },
    {
      root: rootEl,
      rootMargin: '0px 0px 600px 0px',
      threshold: 0.01,
    }
  );

  infiniteObserver.observe(infiniteScrollSentinel.value);
}
/**
 * Save / Unsave handlers: mirror Discussion tab behavior
 *  – do the API call, THEN flip local isSaved so the menu + bookmark update.
 */
const handleSavePostFromYourPosts = async ({ postId }) => {
  if (!postId) return;
  try {
    const res = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/posts/${encodeURIComponent(
        postId
      )}/save`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      console.error(
        '[NewsfeedCreatorYourPostsTab] save backend NOT OK:',
        res.status,
        text
      );
      return;
    }
    await res.json().catch(() => ({}));
    // ✅ mark as saved locally
    savedIds.add(String(postId));
    yourPosts.value = yourPosts.value.map((p) =>
      String(p.id) === String(postId)
        ? { ...p, isSaved: true }
        : p
    );
  } catch (err) {
    console.error(
      '[NewsfeedCreatorYourPostsTab] error saving post:',
      err
    );
  }
};
const handleUnsavePostFromYourPosts = async ({ postId }) => {
  if (!postId) return;
  try {
    const res = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/posts/${encodeURIComponent(
        postId
      )}/save`,
      {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      console.error(
        '[NewsfeedCreatorYourPostsTab] unsave backend NOT OK:',
        res.status,
        text
      );
      return;
    }
    await res.json().catch(() => ({}));
    // ✅ drop from local savedIds + flip flag
    savedIds.delete(String(postId));
    yourPosts.value = yourPosts.value.map((p) =>
      String(p.id) === String(postId)
        ? { ...p, isSaved: false }
        : p
    );
  } catch (err) {
    console.error(
      '[NewsfeedCreatorYourPostsTab] error unsaving post:',
      err
    );
  }
};
/**
 * 🔹 Delete flow for Your Posts
 *  1) child emits @delete-post → open modal
 *  2) cancel → close modal
 *  3) confirm → DELETE backend + remove from list
 */
const handleDeletePostFromYourPosts = ({ postId }) => {
  if (!postId) return;
  deleteTargetPostId.value = postId;
  deleteModalOpen.value = true;
};
const handleCancelDelete = () => {
  deleteModalOpen.value = false;
  deleteTargetPostId.value = null;
  isDeletingPost.value = false;
};
const handleConfirmDelete = async () => {
  const postId = deleteTargetPostId.value;
  if (!postId) return;
  isDeletingPost.value = true;
  try {
    const resp = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/posts/${postId}`,
      {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok || !data.success) {
      console.error(
        '[NewsfeedCreatorYourPostsTab] delete post failed:',
        resp.status,
        data
      );
      // optional: toast
      return;
    }
    // Remove from local list so UI updates immediately
    yourPosts.value = yourPosts.value.filter(
      (p) => String(p.id) !== String(postId)
    );
    // Also keep savedIds clean
    savedIds.delete(String(postId));
    deleteModalOpen.value = false;
    deleteTargetPostId.value = null;
  } catch (err) {
    console.error('[NewsfeedCreatorYourPostsTab] error deleting post:', err);
    // optional: toast
  } finally {
    isDeletingPost.value = false;
  }
};
async function reloadPosts() {
  console.log('[NewsfeedCreatorYourPostsTab] reloadPosts() called', {
    featureInstanceId: props.featureInstanceId,
  });
  if (!props.featureInstanceId) {
    // Same behavior as when featureInstanceId becomes null
    yourPosts.value = [];
    hasMore.value = false;
    offset.value = 0;
    isLoading.value = false;
    isLoadingMore.value = false;
    savedIds = new Set();
    return;
  }
  // Reset local saved cache + re-fetch fresh data from backend
  savedIds = new Set();
  await fetchSavedIdsForInstance(props.featureInstanceId);
  await fetchYourPosts({ reset: true });
}
// 🔹 Initial load: get saved IDs first, then your posts
onMounted(async () => {
  console.log('[NewsfeedCreatorYourPostsTab] onMounted', {
    featureInstanceId: props.featureInstanceId,
    postingAsAvatarUrl: props.postingAsAvatarUrl,
  });
  if (props.featureInstanceId) {
    await fetchSavedIdsForInstance(props.featureInstanceId);
    await fetchYourPosts({ reset: true });
  }
  await nextTick();
  setupInfiniteScrollObserver();
});
// When the instance changes: clear cache, reload saved IDs + posts
watch(
  () => props.featureInstanceId,
  async (newVal, oldVal) => {
    console.log('[NewsfeedCreatorYourPostsTab] featureInstanceId changed', {
      oldVal,
      newVal,
    });
    if (!newVal) {
      yourPosts.value = [];
      hasMore.value = false;
      offset.value = 0;
      isLoading.value = false;
      isLoadingMore.value = false;
      inflightAppendOffset = null;
      savedIds = new Set();
      return;
    }
    if (newVal !== oldVal) {
      sentinelReadyForNextLoad = true;
      inflightAppendOffset = null;
      savedIds = new Set();
      await fetchSavedIdsForInstance(newVal);
      await fetchYourPosts({ reset: true });
      await nextTick();
      setupInfiniteScrollObserver();
    }
  }
);
watch(
  () => yourPosts.value.length,
  async () => {
    await nextTick();
    setupInfiniteScrollObserver();
  }
);
watch(
  () => infiniteScrollSentinel.value,
  (el) => {
    if (el) setupInfiniteScrollObserver();
    else if (infiniteObserver) {
      infiniteObserver.disconnect();
      infiniteObserver = null;
    }
  }
);

onBeforeUnmount(() => {
  if (infiniteObserver) {
    infiniteObserver.disconnect();
    infiniteObserver = null;
  }
  inflightAppendOffset = null;
  sentinelReadyForNextLoad = true;
});
defineExpose({
  reloadPosts,
  addOrUpdatePostFromCreate,
  addOrUpdatePostFromUpdate,
});
</script>
